import React, { ChangeEvent, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import closeIcon from "../assests/close.svg";
import CustomInput from "../components/CustomInput";
import FormSection from "../components/form-section";
import CustomRadioButton from "../components/CustomRadioButton";
import CustomSelect from "../components/CustomSelect";
import CustomButton from "../components/CustomButton";
import { ParseErrors } from "../utils/ParseErrors";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import Loader from "../components/Loader";
import { useDebounceCallback } from "usehooks-ts";
import { useChaseEditModel } from "../store/use-chase-edit-model";
import {
  brandCodes,
  branddata,
  loanStatusData,
} from "../components/chaseListComponents/chaseListData";
import GetCancellationReason, {
  cancellationReasons,
} from "../utils/get-cancellation-reason";
import CanculateChaseOutstandingBalance from "../utils/canculate-chase-outstanding-balance";
import moment from "moment";
import GetUser from "../utils/getUser";

interface IEditChaseModel {
  UpdateItemData: () => void;
}

const EditChaseModel: React.FC<IEditChaseModel> = ({ UpdateItemData }) => {
  const user = GetUser();
  const { initialValues, onClose } = useChaseEditModel();
  const [values, setValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [outStandingAmount, setOutstandingAmount] = useState(0);

  // --------------------------------- [ HANDLE SUBMIT ]-------------------------------------

  const updateChase = async () => {
    if (validate()) {
      setLoading(true);
      try {
        await axiosInstance.patch(`/loan/${initialValues?.id}`, {
          ...values,
        });
        UpdateItemData();
        toast.success("Loan Updated Successfully");
        onClose();
        setLoading(false);
      } catch (error) {
        toast.error(ParseErrors(error));
        setLoading(false);
        console.log(error);
      }
    }
  };

  // ---------------------------------- [ UPDATE INPUT VALUES ] --------------------------------

  const HandleChangeValues = useDebounceCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    },
    200
  );

  const GetDefaultValues = (name: string) => {
    return (
      (typeof values[name] === "number"
        ? Number(values[name])?.toFixed(2)
        : values[name]) ||
      (typeof initialValues?.data[name] === "number"
        ? Number(initialValues?.data[name])?.toFixed(2)
        : initialValues?.data[name])
    );
  };

  // ------------------------------------------------------------------------- PERFORMING COUNT LOGIC -------------------------------------------------------

  let totalAmount = outStandingAmount
    ? Number(
        Math.abs(outStandingAmount) +
          Number(GetDefaultValues("partialPaymentAmount"))
      )
    : Number(GetDefaultValues("totalAmount"));

  useEffect(() => {
    setOutstandingAmount(
      CanculateChaseOutstandingBalance({
        insurerRefund: Number(GetDefaultValues("insurerRefund")) || 0,
        reclaimedInceptionDiscount:
          Number(GetDefaultValues("reclaimedInceptionDiscount")) || 0,
        cancelFee: Number(GetDefaultValues("cancelFee")) || 0,
        unusedAddOnsRefund: Number(GetDefaultValues("unusedAddOnsRefund")) || 0,
        outstandingBalanceofFinanceAgreement:
          Number(GetDefaultValues("outstandingBalanceofFinanceAgreement")) || 0,
        partialPaymentAmount:
          Number(GetDefaultValues("partialPaymentAmount")) || 0,
      })
    );
  }, [values]);

  // ------------------------------------------------------------------- UPDATING ORIGNAL VALUES ---------------------------------------------------------
  useEffect(() => {
    if (
      values?.insurerRefund ||
      values?.reclaimedInceptionDiscount ||
      values?.cancelFee ||
      values?.unusedAddOnsRefund ||
      values?.outstandingBalanceofFinanceAgreement ||
      values?.partialPaymentAmount
    ) {
      setValues((prev) => ({
        ...prev,
        outstandingBalance: outStandingAmount,
        totalAmount: totalAmount,
      }));
    }
  }, [outStandingAmount]);

  const validate = () => {
    if (
      values?.status === "On-Hold" &&
      !values?.restartDate &&
      !initialValues?.data?.restartDate
    ) {
      toast.error("Restart Date is required if you want to put chase on hold");
      return false;
    } else {
      return true;
    }
  };

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !loading) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [loading, onClose]);

  return createPortal(
    <div
      className="z-[99999] w-screen h-screen fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 max-sm:py-4 max-sm:px-4 animate-fade-in"
      onClick={() => !loading && onClose()}
    >
      <div
        className="bg-surface border border-edge shadow-overlay w-[850px] max-w-full rounded-2xl animate-fade-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/*---------------------------- [ HEADER ] ------------------------------- */}
        <div className="flex items-start justify-between gap-4 py-4 px-6 border-b border-edge bg-surface rounded-t-2xl">
          <div className="min-w-0">
            <h2 className="text-[20px] font-bold text-text-primary leading-tight">
              Update chase
            </h2>
            {/* Names the record being edited — a bare "Update chase" gives no
                confirmation that the right row was opened. */}
            <p className="mt-1 text-[13px] font-medium text-light-gray truncate">
              {[
                [initialValues?.data?.firstName, initialValues?.data?.surname]
                  .filter(Boolean)
                  .join(" "),
                initialValues?.data?.policyReferenceNumber,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            title="Close"
            className="shrink-0 h-9 w-9 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay-hover transition-colors duration-200 cursor-pointer"
          >
            <FontAwesomeIcon icon={faXmark} className="text-[17px]" />
          </button>
        </div>
        {/*---------------------------- [ HEADER ] ------------------------------- */}

        {/* -------------------------- [ BODY ] ------------------------------------ */}
        <div className="px-6 py-5 max-sm:px-4 flex flex-col gap-4">
          {/* ------------------------------ CUSTOMER ------------------------------ */}
          <FormSection title="Customer" hint="Who the chase is for and how to reach them.">
            <CustomInput
              autoComplete="none"
              label="First Name"
              defaultValue={GetDefaultValues("firstName")}
              name="firstName"
              onChange={HandleChangeValues}
            />
            <CustomInput
              autoComplete="none"
              label="Surname"
              defaultValue={GetDefaultValues("surname")}
              name="surname"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Email Address"
              autoComplete="none"
              defaultValue={GetDefaultValues("email")}
              name="email"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Phone Number"
              autoComplete="none"
              defaultValue={GetDefaultValues("phone")}
              name="phone"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Date of Birth"
              autoComplete="none"
              defaultValue={GetDefaultValues("dob")}
              name="dob"
              type="date"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Post Code"
              autoComplete="none"
              defaultValue={GetDefaultValues("postCode")}
              name="postCode"
              className="uppercase"
              onChange={HandleChangeValues}
            />
          </FormSection>

          {/* ------------------------------ ADDRESS ------------------------------ */}
          <FormSection title="Address">
            <CustomInput
              label="Address 1"
              autoComplete="none"
              defaultValue={GetDefaultValues("address1")}
              name="address1"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 2"
              autoComplete="none"
              defaultValue={GetDefaultValues("address2")}
              name="address2"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 3"
              autoComplete="none"
              defaultValue={GetDefaultValues("address3")}
              name="address3"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 4"
              autoComplete="none"
              defaultValue={GetDefaultValues("address4")}
              name="address4"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Address 5"
              autoComplete="none"
              defaultValue={GetDefaultValues("address5")}
              name="address5"
              onChange={HandleChangeValues}
            />
          </FormSection>

          {/* ------------------------------ POLICY ------------------------------ */}
          <FormSection title="Policy" hint="Where the chase came from and what it relates to.">
            <div>
              <CustomSelect
                comboboxType="select"
                label="Chase Status"
                labelProperty="name"
                showTop
                valueProperty="name"
                options={loanStatusData}
                onSingleSelectChange={(option) =>
                  setValues((prev) => ({ ...prev, status: option?.name }))
                }
                value={loanStatusData.find(
                  (type) => type.name === GetDefaultValues("status")
                )}
                disabled={
                  (initialValues?.data?.log?.attempt <
                    Number(
                      initialValues.data?.log?.paymentPlan.split("-")[0]
                    ) &&
                    initialValues.data.status === "On-Hold") ||
                  (initialValues?.data?.status === "Debt-Collectors" &&
                    !["4W", "CD"]?.includes(user?.code))
                }
              />

              {GetDefaultValues("status") === "On-Hold" ? (
                <div className="mt-4">
                  <CustomInput
                    label="Restart Date"
                    autoComplete="none"
                    defaultValue={moment(
                      GetDefaultValues("restartDate")
                    ).format("YYYY-MM-DD")}
                    name="restartDate"
                    type="date"
                    min={moment(
                      new Date().setDate(new Date().getDate() + 1)
                    ).format("YYYY-MM-DD")}
                    onChange={HandleChangeValues}
                  />
                </div>
              ) : null}
            </div>

            <CustomSelect
              comboboxType="select"
              label="Brand"
              labelProperty="label"
              showTop
              valueProperty="name"
              options={branddata}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, brand: option?.name }))
              }
              value={branddata.find(
                (type) => type.name === GetDefaultValues("brand")
              )}
            />
            <CustomSelect
              comboboxType="select"
              label="Affinity"
              labelProperty="value"
              showTop
              valueProperty="value"
              options={brandCodes}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({ ...prev, brandCode: option?.value }))
              }
              value={brandCodes.find(
                (type) => type.value === GetDefaultValues("brandCode")
              )}
            />
            <CustomInput
              label="Agent Code"
              autoComplete="none"
              defaultValue={GetDefaultValues("schemeCode")}
              name="schemeCode"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Policy Reference Number"
              autoComplete="none"
              defaultValue={GetDefaultValues("policyReferenceNumber")}
              name="policyReferenceNumber"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Insurer Name"
              autoComplete="none"
              defaultValue={GetDefaultValues("insurerName")}
              name="insurerName"
              onChange={HandleChangeValues}
            />
            <CustomInput
              label="Vehicle Reg No"
              autoComplete="none"
              defaultValue={GetDefaultValues("vehicleReg")}
              name="vehicleReg"
              onChange={HandleChangeValues}
              className="uppercase font-heading-font"
            />
            <CustomSelect
              comboboxType="select"
              label="Reason For Cancellation"
              labelProperty="label"
              showTop
              valueProperty="code"
              options={cancellationReasons}
              onSingleSelectChange={(option) =>
                setValues((prev) => ({
                  ...prev,
                  reasonForCancellation: option?.code,
                }))
              }
              value={GetCancellationReason(
                GetDefaultValues("reasonForCancellation")
              )}
            />
          </FormSection>

          {/* ------------------------------ AMOUNTS ------------------------------ */}
          <FormSection
            title="Amounts"
            hint="Total and outstanding balance are calculated from the figures above."
          >
            <CustomInput
              label="Insurer Refund"
              autoComplete="none"
              defaultValue={GetDefaultValues("insurerRefund")}
              name="insurerRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Reclaimed Inception Discount"
              autoComplete="none"
              defaultValue={GetDefaultValues("reclaimedInceptionDiscount")}
              name="reclaimedInceptionDiscount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Outstanding Balance of Finance Agreement"
              autoComplete="none"
              defaultValue={GetDefaultValues(
                "outstandingBalanceofFinanceAgreement"
              )}
              name="outstandingBalanceofFinanceAgreement"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Cancellation Fee"
              autoComplete="none"
              defaultValue={GetDefaultValues("cancelFee")}
              name="cancelFee"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Partial Payment Amount"
              autoComplete="none"
              defaultValue={GetDefaultValues("partialPaymentAmount")}
              name="partialPaymentAmount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Commissions/Addons Refund"
              autoComplete="none"
              defaultValue={GetDefaultValues("unusedAddOnsRefund")}
              name="unusedAddOnsRefund"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={
                GetDefaultValues("status") !== "Awaiting-Customer" &&
                !["SA", "CR"]?.includes(user?.code)
              }
            />
            <CustomInput
              label="Total Amount"
              autoComplete="none"
              value={Math.abs(totalAmount)?.toFixed(2)}
              name="totalAmount"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              disabled={true}
            />
            <CustomInput
              label="Outstanding Balance"
              autoComplete="none"
              value={
                Math.abs(outStandingAmount)?.toFixed(2) ||
                GetDefaultValues("outstandingBalance")
              }
              name="outstandingBalance"
              type="number"
              onChange={HandleChangeValues}
              forceDecimal
              startIcon="£"
              negativeLabel={outStandingAmount < 0}
              disabled={true}
              error={
                Number(outStandingAmount.toFixed(2)) > 0
                  ? "Invalid Outstanding Balance"
                  : null
              }
            />
          </FormSection>

          {/* --------------------------- CLASSIFICATION --------------------------- */}
          <FormSection title="Classification">
            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Policy Type
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="standard"
                  name="policyType"
                  label="Standard"
                  checked={GetDefaultValues("policyType") === "Standard"}
                  onChange={HandleChangeValues}
                  value="Standard"
                />
                <CustomRadioButton
                  id="Nonstandard"
                  name="policyType"
                  label="Non-Standard"
                  value="Non-Standard"
                  checked={GetDefaultValues("policyType") === "Non-Standard"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Department
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="sales"
                  name="bussinessType"
                  label="Sales"
                  checked={GetDefaultValues("bussinessType") === "Sales"}
                  onChange={HandleChangeValues}
                  value="Sales"
                />
                <CustomRadioButton
                  id="Renewals"
                  name="bussinessType"
                  label="Renewals"
                  value="Renewals"
                  checked={GetDefaultValues("bussinessType") === "Renewals"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>

            {/* Chase Age was settable on create but had no edit control at all, so a
                wrong value could never be corrected. It drives the ICB fee split
                (new charges at 20% vs old at 40%), so it is not cosmetic. */}
            <div>
              <label className="text-[12px] font-medium text-light-gray block mb-1.5">
                Chase Age
              </label>
              <div className="flex items-center gap-5 h-10 max-sm:flex-wrap max-sm:h-auto max-sm:gap-3 max-sm:py-2">
                <CustomRadioButton
                  id="edit-age-new"
                  name="age"
                  label="New"
                  value="new"
                  checked={GetDefaultValues("age") === "new"}
                  onChange={HandleChangeValues}
                />
                <CustomRadioButton
                  id="edit-age-old"
                  name="age"
                  label="Old"
                  value="old"
                  checked={GetDefaultValues("age") === "old"}
                  onChange={HandleChangeValues}
                />
              </div>
            </div>
          </FormSection>
        </div>

        <div className="border-t border-edge bg-surface px-6 py-4 rounded-b-2xl">
          <CustomButton
            variant="primary"
            fullWidth
            onClick={updateChase}
            disabled={loading || Number(outStandingAmount.toFixed(2)) > 0}
          >
            {loading ? <Loader /> : "Update Chase"}
          </CustomButton>
        </div>
        {/* -------------------------- [ BODY ] ------------------------------------ */}
      </div>
    </div>,
    document.body
  );
};

export default EditChaseModel;
